/**
 * Bottom Purchase Bar
 * ===================
 * Handles:
 * - Live sync on cart update (non smart-buy-button sources)
 * - Bundle checkout button click (subscription step 1)
 * - Switch-to-subscription button click (onetime mode → redirect to subscription step 1)
 * - UI state management for loading states
 *
 * Note on architecture:
 * - The bar is a Shopify section and can be re-rendered by AJAX (innerHTML replace).
 * - When that happens, we must re-bind click handlers and let bundle-subscription re-apply
 *   JS-calculated counter/total (bundle selection lives in-memory on step1).
 */
(function () {
  let couponDismissed = false;

  function syncCouponNotice(bar = document.querySelector('.bottom-purchase-bar')) {
    const notice = bar?.querySelector('[data-coupon-notice]');
    if (!notice) return;
    notice.hidden = couponDismissed || notice.dataset.awaitsBundle === 'true' || !(Number(notice.dataset.savings) > 0);
  }

  function dismissCouponNotice(event) {
    const button = event.target.closest('[data-action="dismiss-coupon"]');
    if (!button) return;
    couponDismissed = true;
    syncCouponNotice(button.closest('.bottom-purchase-bar'));
  }

  function formatCouponMoney(cents, format) {
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
      const [precision, thousands, decimal] = formats[name] || formats.amount;
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (Number(fraction) ? decimal + fraction : '');
    });
    const template = document.createElement('template');
    template.innerHTML = formatted;
    return template.content.textContent;
  }

  function updateBundleCouponNotice(event) {
    const { bar, totalPrice, couponSavings } = event.detail || {};
    if (!bar?.isConnected || !['subscription_step_1', 'subscription_step_2'].includes(bar.dataset.mode)) return;
    if (!Number.isFinite(totalPrice) || !Number.isFinite(couponSavings)) return;
    const notice = bar.querySelector('[data-coupon-notice]');
    if (!notice) return;
    const total = totalPrice + Number(notice.dataset.extrasTotal || 0);
    const savings = couponSavings + Number(notice.dataset.extrasSavings || 0);
    notice.dataset.total = total;
    notice.dataset.savings = savings;
    notice.dataset.awaitsBundle = 'false';
    for (const [selector, amount] of [
      ['[data-coupon-before]', total + savings],
      ['[data-coupon-now]', total],
      ['[data-coupon-savings]', savings],
    ]) {
      const element = notice.querySelector(selector);
      if (element) element.textContent = formatCouponMoney(amount, notice.dataset.moneyFormat);
    }
    syncCouponNotice(bar);
  }

  document.addEventListener('click', dismissCouponNotice);
  window.addEventListener('bottom-purchase-bar:refreshed', () => syncCouponNotice());
  window.addEventListener('bundle:updated', updateBundleCouponNotice);

  /**
   * Get section ID for the bottom purchase bar
   * @returns {string|null} Section ID
   */
  function getBarSectionId() {
    const bar = document.querySelector('.bottom-purchase-bar-section');
    const wrapper = bar?.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  /**
   * Refresh bar section from server
   * Used when cart changes outside smart-buy-button (e.g. cart drawer quantity edits).
   */
  function refreshBar() {
    const sectionId = getBarSectionId();
    if (!sectionId) return;

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const wrapper = document.querySelector('.bottom-purchase-bar-section')?.closest('[id^="shopify-section-"]');
        const sectionSelector = '#shopify-section-' + CSS.escape(sectionId);
        const sourceSection = doc.querySelector(sectionSelector);
        if (wrapper && sourceSection) {
          wrapper.innerHTML = sourceSection.innerHTML;
          // Re-bind event listeners after DOM update
          bindBundleCheckoutButton();
          bindOpenCartButton();
          bindSwitchToSubscriptionButton();
          // Notify other scripts that the bar DOM was replaced (e.g. bundle-subscription).
          // This triggers bundle-subscription.js to re-apply JS-calculated counter/total values.
          window.dispatchEvent(new CustomEvent('bottom-purchase-bar:refreshed'));
          // Best-effort: also try to update immediately (bundle-subscription listener will also handle it).
          if (window.BundleSubscription && typeof window.BundleSubscription.updatePurchaseBar === 'function') {
            window.BundleSubscription.updatePurchaseBar();
          }
        }
      })
      .catch((err) => console.error('Bottom purchase bar refresh failed:', err));
  }

  /**
   * Handle bundle checkout button click
   * Triggers BundleCheckout.convertAndSubmit() for Recharge conversion
   * @param {Event} event - Click event
   */
  async function handleBundleCheckout(event) {
    const button = event.target.closest('[data-action="bundle-checkout"]');
    if (!button) return;

    window.BundleSubscription?.updateContinueButton?.(button.closest('.bottom-purchase-bar'));
    if (button.dataset.modalOpen) {
      event.preventDefault();
      return;
    }

    if (button.classList.contains('is-disabled') || button.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    // Prevent double-click
    if (button.classList.contains('loading')) {
      event.preventDefault();
      return;
    }

    // Get redirect URL from button data
    const redirectUrl = button.dataset.redirectUrl || '/pages/subscription-step-2';

    // Check if BundleCheckout is available
    if (!window.BundleCheckout) {
      console.error('BundleCheckout not available');
      // Fallback: just navigate
      window.location.href = redirectUrl;
      return;
    }

    // Show loading state
    button.classList.add('loading');
    const spinner = button.querySelector('.bottom-purchase-bar__continue-spinner');
    const text = button.querySelector('.bottom-purchase-bar__continue-text');
    if (spinner) spinner.classList.remove('hidden');
    if (text) text.style.opacity = '0.5';

    try {
      // Convert bundle and submit to cart (convertAndSubmit does not redirect; we do it after clearing state)
      const success = await window.BundleCheckout.convertAndSubmit(redirectUrl);

      if (!success) {
        // Remove loading state on failure
        clearContinueButtonLoading(button, spinner, text);
        return;
      }

      // "Finally before redirect": clear loading state so if user navigates back (bfcache),
      // the page is restored with button ready. Then redirect after one tick so DOM can update.
      clearContinueButtonLoading(button, spinner, text);
      requestAnimationFrame(() => {
        window.location.href = redirectUrl;
      });
    } catch (error) {
      console.error('Bundle checkout failed:', error);
      clearContinueButtonLoading(button, spinner, text);
    }
  }

  /**
   * Clear loading state from the continue button (used on failure and before redirect).
   */
  function clearContinueButtonLoading(button, spinner, text) {
    if (!button) return;
    button.classList.remove('loading');
    if (spinner) spinner.classList.add('hidden');
    if (text) text.style.opacity = '1';
  }

  /**
   * Bind click handler to bundle checkout button
   */
  function bindBundleCheckoutButton() {
    const button = document.querySelector('[data-action="bundle-checkout"]');
    if (!button) return;

    // Remove existing listener to prevent duplicates
    button.removeEventListener('click', handleBundleCheckout);
    button.addEventListener('click', handleBundleCheckout);
  }

  /**
   * Handle "open cart" button click (step 3: onetime_step_3 / subscription_step_3).
   * Opens the cart drawer instead of navigating to checkout.
   * @param {Event} event - Click event
   */
  function handleOpenCart(event) {
    const button = event.target.closest('[data-action="open-cart"]');
    if (!button) return;
    event.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  /**
   * Bind click handler to open-cart buttons (step 3 bar).
   */
  function bindOpenCartButton() {
    document.querySelectorAll('[data-action="open-cart"]').forEach((btn) => {
      btn.removeEventListener('click', handleOpenCart);
      btn.addEventListener('click', handleOpenCart);
    });
  }

  /**
   * Handle "switch to subscription" button click (onetime → subscription).
   * Saves eligible onetime primary items as a bundleBasket in
   * sessionStorage so subscription-step-1 can pre-populate the selection.
   * Removes only those cart lines and redirects to subscription-step-1.
   *
   * @param {Event} event - Click event
   */
  let isSwitchingToSubscription = false;

  async function handleSwitchToSubscription(event) {
    const button = event.target.closest('[data-action="switch-to-subscription"]');
    if (!button) return;
    event.preventDefault();

    if (isSwitchingToSubscription || button.disabled || button.getAttribute('aria-busy') === 'true') {
      return;
    }

    const url = button.dataset.redirectUrl || '/pages/subscription-step-1';

    isSwitchingToSubscription = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');

    try {
      sessionStorage.removeItem('pendingBundleBasket');
      const jsonEl = document.getElementById('convertible-bundle-items');
      if (!jsonEl) throw new Error('Convertible cart items are unavailable');

      const items = JSON.parse(jsonEl.textContent);
      if (!Array.isArray(items) || items.some((item) =>
        !item || !item.variantId || !item.lineKey || !Number.isInteger(item.quantity) || item.quantity <= 0
      )) {
        throw new Error('Invalid convertible cart items');
      }

      if (items.length === 0) {
        window.location.href = url;
        return;
      }

      const basket = {};
      const updates = {};
      items.forEach(({ lineKey, ...item }) => {
        updates[lineKey] = 0;
        if (basket[item.variantId]) {
          basket[item.variantId].quantity += item.quantity;
        } else {
          basket[item.variantId] = item;
        }
      });
      sessionStorage.setItem('pendingBundleBasket', JSON.stringify(basket));

      const updateUrl = typeof routes !== 'undefined' && routes.cart_update_url
        ? `${routes.cart_update_url}.js`
        : '/cart/update.js';
      const res = await fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && !data.errors && !data.status) {
        // Skip publishing cartUpdate: we're navigating away immediately.
        // Publishing would trigger refreshBar → guard redirect race on step 2/3.
        window.location.href = url;
        return;
      }
      throw new Error(data.description || data.errors || 'Cart update failed');
    } catch (err) {
      sessionStorage.removeItem('pendingBundleBasket');
      console.error('Switch to subscription failed:', err);
    } finally {
      isSwitchingToSubscription = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('loading');
    }
  }

  /**
   * Bind click handler to ALL switch-to-subscription buttons on the page.
   * Covers: bottom-purchase-bar (onetime steps) and rich-text-sub-savings (step 3).
   */
  function bindSwitchToSubscriptionButton() {
    document.querySelectorAll('[data-action="switch-to-subscription"]').forEach((button) => {
      button.removeEventListener('click', handleSwitchToSubscription);
      button.addEventListener('click', handleSwitchToSubscription);
    });
  }

  /**
   * Redirect when the user is on a step they’re not allowed to be on.
   * Liquid sets data-guard-redirect (e.g. onetime step 2/3 without enough primary
   * dishes); we run the redirect on init and after every bar AJAX refresh.
   */
  function redirectWhenStepNotAllowed() {
    const bar = document.querySelector('.bottom-purchase-bar');
    const url = bar?.dataset?.guardRedirect;
    if (url) window.location.replace(url);
  }

  /**
   * Initialize bottom purchase bar
   */
  function init() {
    syncCouponNotice();
    // Bind bundle checkout button
    bindBundleCheckoutButton();
    // Bind open-cart button (step 3: opens drawer instead of checkout)
    bindOpenCartButton();
    // Bind switch-to-subscription button (onetime mode)
    bindSwitchToSubscriptionButton();
    // Redirect to step 1 when requirements for current step aren’t met (e.g. onetime step 2/3)
    redirectWhenStepNotAllowed();

    // After bar is re-rendered by AJAX, re-bind handlers and re-check step access.
    window.addEventListener('bottom-purchase-bar:refreshed', bindBundleCheckoutButton);
    window.addEventListener('bottom-purchase-bar:refreshed', bindOpenCartButton);
    window.addEventListener('bottom-purchase-bar:refreshed', bindSwitchToSubscriptionButton);
    window.addEventListener('bottom-purchase-bar:refreshed', redirectWhenStepNotAllowed);

    // Subscribe to cart updates (if pubsub available)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Skip refresh if triggered by smart-buy-button (already handled)
        if (event?.source === 'smart-buy-button') return;
        refreshBar();
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', init);
})();
